import { faImageUser, faMicrophone, faUniversalAccess, faVideo } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { DrawerBody, DrawerTitle, Subhead, Tab, Tabs, cn, useRebrand } from '@growtherapy/sprout-ui';

import { useTheme } from '../../hooks/useTheme';
import { Theme } from '../../state';
import { DrawerHeader } from '../DrawerHeader';

export enum SettingsDrawerTab {
  VIDEO = 'video',
  AUDIO = 'audio',
  BACKGROUND = 'background',
  ACCESSIBILITY = 'accessibility',
}

export type TabMap = Record<SettingsDrawerTab, JSX.Element | undefined>;

export function SettingsDrawer({ tabMap }: { tabMap: TabMap }) {
  const t = useTranslate();
  const [selectedTab, setSelectedTab] = useState<SettingsDrawerTab>(SettingsDrawerTab.VIDEO);
  const isRebrand = useRebrand();
  const { theme } = useTheme();

  const isDarkTheme = theme === Theme.DARK;

  return (
    <>
      <DrawerHeader>
        {isRebrand && (
          <Subhead.Small
            className={cn({
              'rebrand:text-text-inverse': isDarkTheme,
            })}
          >
            {t('telehealth-core:settings.settings')}
          </Subhead.Small>
        )}
        {!isRebrand && <DrawerTitle>{t('telehealth-core:settings.settings')}</DrawerTitle>}
      </DrawerHeader>
      <DrawerBody>
        <Tabs
          selectedTabId={selectedTab}
          onSelectTab={(newTab: SettingsDrawerTab) => {
            setSelectedTab(newTab);
          }}
          className='w-full overflow-x-scroll no-scrollbar flex gap-2'
        >
          {/**
           * Fragmnets used below because the Tabs component is very strict
           * about its child types - null, undefined, etc. are not allowed
           */}
          {SettingsDrawerTab.VIDEO in tabMap ? (
            <Tab
              tabId='video'
              className={cn('whitespace-nowrap', {
                'rebrand:text-text-inverse rebrand:hover:text-text-inverse rebrand:hover:border-b-text-inverse':
                  isDarkTheme,
                'rebrand:border-b-text-inverse ': isDarkTheme && selectedTab === SettingsDrawerTab.VIDEO,
              })}
              icon={faVideo}
              title={t('telehealth-core:settings.video')}
            />
          ) : (
            <></>
          )}
          {SettingsDrawerTab.AUDIO in tabMap ? (
            <Tab
              tabId='audio'
              className={cn('whitespace-nowrap', {
                'rebrand:text-text-inverse rebrand:hover:text-text-inverse rebrand:hover:border-b-text-inverse':
                  isDarkTheme,
                'rebrand:border-b-text-inverse ': isDarkTheme && selectedTab === SettingsDrawerTab.AUDIO,
              })}
              icon={faMicrophone}
              title={t('telehealth-core:settings.audio')}
            />
          ) : (
            <></>
          )}
          {SettingsDrawerTab.BACKGROUND in tabMap ? (
            <Tab
              tabId='background'
              className={cn('whitespace-nowrap', {
                'rebrand:text-text-inverse rebrand:hover:text-text-inverse rebrand:hover:border-b-text-inverse':
                  isDarkTheme,
                'rebrand:border-b-text-inverse ': isDarkTheme && selectedTab === SettingsDrawerTab.BACKGROUND,
              })}
              icon={faImageUser}
              title={t('telehealth-core:settings.background')}
            />
          ) : (
            <></>
          )}
          {SettingsDrawerTab.ACCESSIBILITY in tabMap ? (
            <Tab
              tabId='accessibility'
              className={cn('whitespace-nowrap', {
                'rebrand:text-text-inverse rebrand:hover:text-text-inverse rebrand:hover:border-b-text-inverse':
                  isDarkTheme,
                'rebrand:border-b-text-inverse ': isDarkTheme && selectedTab === SettingsDrawerTab.ACCESSIBILITY,
              })}
              icon={faUniversalAccess}
              title={t('telehealth-core:settings.accessibility')}
            />
          ) : (
            <></>
          )}
        </Tabs>
        {tabMap[selectedTab]}
      </DrawerBody>
    </>
  );
}
